import React, { useState } from 'react';
import {
    Terminal, Play, Shield, Zap, Mouse, Eye, FileText, Copy,
    Globe, Search, ArrowRight, Layout, Database, Bug, X, MessageSquare
} from 'lucide-react';
import useStore from '../store';

const CommandToolbar = ({ onClose }) => {
    const { sendCommand, targetId } = useStore();
    const [urlInput, setUrlInput] = useState('');
    const [activeCategory, setActiveCategory] = useState('ALL');

    const handleNavigate = (e) => {
        e.preventDefault();
        if (!urlInput.trim() || !targetId) return;
        let url = urlInput;
        if (!url.startsWith('http')) url = 'https://' + url;
        sendCommand('NAVIGATE', { url });
    };

    const categories = [
        { id: 'ALL', label: 'All', icon: Layout },
        { id: 'DOUDIAN', label: 'Doudian', icon: MessageSquare },
        { id: 'INTERACT', label: 'Interact', icon: Mouse },
        { id: 'EXTRACT', label: 'Extract', icon: Database },
        { id: 'DEBUG', label: 'Debug', icon: Bug },
        { id: 'FUN', label: 'Fun', icon: Zap },
    ];

    const commands = [
        // Doudian Control
        { name: 'Start Auto Reply', category: 'DOUDIAN', icon: Play, type: 'COMMAND', payload: { command: 'START_AUTO_REPLY' }, desc: 'Enable auto-reply' },
        { name: 'Stop Auto Reply', category: 'DOUDIAN', icon: X, type: 'COMMAND', payload: { command: 'STOP_AUTO_REPLY' }, desc: 'Disable auto-reply' },
        { name: 'Get Status', category: 'DOUDIAN', icon: FileText, type: 'COMMAND', payload: { command: 'GET_STATUS' }, desc: 'Check extension status' },
        { name: 'Scan Selectors', category: 'DOUDIAN', icon: Search, type: 'COMMAND', payload: { command: 'SCAN_SELECTORS' }, desc: 'Debug selectors' },

        // Interaction
        { name: 'Click Center', category: 'INTERACT', icon: Mouse, code: `const el = document.elementFromPoint(window.innerWidth/2, window.innerHeight/2); if(el) el.click();` },
        { name: 'Fill Inputs', category: 'INTERACT', icon: FileText, code: `document.querySelectorAll('input').forEach(i => i.value = 'Aether Test');` },
        { name: 'Scroll Bottom', category: 'INTERACT', icon: ArrowRight, code: 'window.scrollTo(0, document.body.scrollHeight)' },
        { name: 'Show Passwords', category: 'INTERACT', icon: Eye, code: `document.querySelectorAll('input[type="password"]').forEach(p => p.type = 'text');` },

        // Extraction
        { name: 'Get Title', category: 'EXTRACT', icon: FileText, code: 'document.title' },
        { name: 'Get Cookies', category: 'EXTRACT', icon: Database, code: 'document.cookie' },
        { name: 'Extract Links', category: 'EXTRACT', icon: Globe, code: `Array.from(document.querySelectorAll('a')).map(a => a.href).join('\\n')` },
        { name: 'Page Metadata', category: 'EXTRACT', icon: FileText, code: `({ url: location.href, title: document.title, links: document.querySelectorAll('a').length })` },

        // Debug
        { name: 'Highlight IDs', category: 'DEBUG', icon: Bug, code: `document.querySelectorAll('[id]').forEach(el => { el.style.outline = '2px solid cyan'; el.setAttribute('data-id-label', el.id); });` },
        { name: 'Console Stats', category: 'DEBUG', icon: Terminal, code: `console.log('Page Stats:', { url: location.href, elements: document.querySelectorAll('*').length });` },
        { name: 'Visual Debug', category: 'DEBUG', icon: Eye, code: `document.body.style.border = "5px solid red";` },

        // Fun / God Mode
        { name: 'Dark Mode', category: 'FUN', icon: Shield, code: `document.body.style.filter = document.body.style.filter === 'invert(1) hue-rotate(180deg)' ? '' : 'invert(1) hue-rotate(180deg)';` },
        { name: 'Edit Mode', category: 'FUN', icon: FileText, code: 'document.body.contentEditable="true"' },
        { name: 'Hide Body', category: 'FUN', icon: Eye, code: 'document.body.style.display="none"' },
        { name: 'Crash Tab', category: 'FUN', icon: Zap, code: 'while(true){}' },
    ];

    const filteredCommands = activeCategory === 'ALL'
        ? commands
        : commands.filter(c => c.category === activeCategory);

    return (
        <div className="w-full bg-gray-900 flex flex-col h-full">
            {/* Header with Close Button */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <div className="flex items-center gap-2">
                    <Terminal size={18} className="text-blue-400" />
                    <h2 className="text-sm font-bold text-gray-100">Command Toolbar</h2>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                        title="Close (Esc)"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            {/* Address Bar */}
            <div className="p-3 border-b border-gray-800">
                <form onSubmit={handleNavigate} className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                    <input
                        type="text"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        placeholder="Enter URL to navigate..."
                        className="w-full bg-gray-800 border border-gray-700 rounded-md py-1.5 pl-9 pr-8 text-xs text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                    <button
                        type="submit"
                        disabled={!targetId}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                    >
                        <ArrowRight size={12} />
                    </button>
                </form>
            </div>

            {/* Category Tabs */}
            <div className="flex p-2 gap-1 overflow-x-auto scrollbar-hide border-b border-gray-800">
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${activeCategory === cat.id
                            ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                            }`}
                    >
                        <cat.icon size={12} />
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Command Grid */}
            <div className="flex-1 overflow-y-auto p-2">
                <div className="grid grid-cols-1 gap-2">
                    {filteredCommands.map((cmd, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                if (cmd.code) {
                                    sendCommand('EVAL', { code: cmd.code, id: Date.now() });
                                } else if (cmd.type) {
                                    sendCommand(cmd.type, { ...cmd.payload, id: Date.now() });
                                }
                            }}
                            disabled={!targetId}
                            className="group flex items-center gap-3 p-2 rounded-lg bg-gray-800/50 border border-gray-800 hover:bg-gray-800 hover:border-gray-700 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className={`p-2 rounded-md bg-gray-900 group-hover:bg-gray-700 transition-colors ${cmd.category === 'FUN' ? 'text-yellow-400' :
                                cmd.category === 'DEBUG' ? 'text-red-400' :
                                    cmd.category === 'EXTRACT' ? 'text-green-400' : 'text-blue-400'
                                }`}>
                                <cmd.icon size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-200 group-hover:text-white truncate">
                                    {cmd.name}
                                </div>
                                <div className="text-[10px] text-gray-500 truncate font-mono opacity-60">
                                    {cmd.code}
                                </div>
                            </div>
                            <Play size={12} className="text-gray-600 group-hover:text-white opacity-0 group-hover:opacity-100 transition-all" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CommandToolbar;
